import EventDispatcher from "ui/EventDispatcher";
import Utils from "ui/helpers/Utils";
import ImageTransform from "ui/canvas/ImageTransform";
import {STATIC_URL} from "ui/config";
import {CanvasBlendingMode, CanvasBlendingModeToCompositeOperation} from "ui/canvas/CanvasBlendingMode";

/**
 * Event: Fills the selected frame
 *
 * @type {string}
 */
export const EVENT_FILL_FRAME = 'fill_frame';

/**
 * Event: All images in a frame are completely loaded
 *
 * @type {string}
 */
export const EVENT_FRAME_LOADED = 'frame_loaded';

/**
 * Event: The layer changed visibility flag
 *
 * @type {string}
 */
export const EVENT_VISIBILITY_CHANGED = 'visibility_changed';

/**
 * @type {HTMLImageElement}
 */
const BLANK_IMAGE = new Image();
BLANK_IMAGE.crossOrigin = 'anonymous';

/**
 * Image Layer Class
 */
export default class CanvasLayer {
	/**
	 * Class constructor
	 *
	 * @param {Canvas} canvas
	 * @param {T_CanvasLayerOptions} [options]
	 */
	constructor(canvas, options = {}) {
		/**
		 * @type {T_CanvasLayerData}
		 */
		let data = options.data || {};

		/**
		 * Layer Id
		 *
		 * @type {string}
		 */
		this._id = data.id || CanvasLayer.generateId();

		/**
		 * @type {EventDispatcher}
		 * @private
		 */
		this._events = new EventDispatcher();

		/**
		 * Version of the layer (each change increases version)
		 *
		 * @type {number}
		 */
		this.version = 0;

		/**
		 * @type {Canvas}
		 * @private
		 */
		this._canvas = canvas;

		/**
		 * @type {string}
		 * @private
		 */
		this._title = data.title || 'Untitled';

		/**
		 * @type {boolean}
		 * @private
		 */
		this._visible = true;

		/**
		 * @type {number}
		 * @private
		 */
		this._opacity = data.opacity || 100;

		/**
		 * @type {string}
		 * @private
		 */
		this._blendingMode = data.blend || CanvasBlendingMode.NORMAL;

		/**
		 * @type {string}
		 * @private
		 */
		this._sourceId = data.source_id || '';

		/**
		 * @type {?T_CanvasLayerSprites}
		 * @private
		 */
		this._sprites = data.sprites || null;

		/**
		 * @type {Array<string>}
		 * @private
		 */
		this._initialIndices = [];

		/**
		 * @type {Array<T_ImageData>}
		 * @private
		 */
		this._timeline = [];

		/**
		 * @type {Array.<T_ImageData>}
		 * @private
		 */
		this._timelineTemp = null;

		/**
		 * @type {boolean}
		 * @private
		 */
		this._isTempMode = false;

		/**
		 * @type {number}
		 * @private
		 */
		this._frame = -1;

		/**
		 * @type {{
		 * loadQueue: Array<function(l:CanvasLayer)>,
		 * cache: Map<string, HTMLImageElement>}}
		 * @private
		 */
		this._images = {
			loadQueue: [],
			cache: new Map(),
		};

		this._handlers = {
			imageLoad: this._onImageLoaded.bind(this)
		};

		/**
		 * @type {?T_StateLayer}
		 * @private
		 */
		this._prevLayerState = null;

		if (this._sourceId !== '') {
			// build timeline for scene by reference with independent URL to each frame image
			this._buildReferenceTimeline(data);
		} else {
			// build simple timeline with image sources as data-uri
			this._buildTimeline(data);
		}
	}

	/**
	 * Generates layer id
	 *
	 * @returns {string}
	 */
	static generateId() {
		return 'l' + Math.ceil(Math.random() * 100000) + 't' + Date.now();
	}

	/**
	 * Returns instance of the events dispatcher
	 *
	 * @returns {EventDispatcher}
	 */
	get events() {
		return this._events;
	}

	/**
	 * Returns layer Id
	 *
	 * @returns {string}
	 */
	getId() {
		return this._id;
	}

	/**
	 * Returns layer title
	 *
	 * @returns {string}
	 */
	getTitle() {
		return this._title;
	}

	/**
	 * Returns TRUE if layer is can be changed
	 *
	 * @returns {boolean}
	 */
	get isOperable() {
		return this._sourceId === '';
	}

	/**
	 * Returns TRUE if layer is reference to another scene
	 *
	 * @returns {boolean}
	 */
	get isReference() {
		return this._sourceId !== '';
	}

	/**
	 * Returns TRUE if layer is visible
	 *
	 * @returns {boolean}
	 */
	isVisible() {
		return this._visible;
	}

	/**
	 * Returns TRUE if the main image loaded
	 *
	 * @param {?number} [index]
	 * @returns {boolean}
	 */
	isLoaded(index = null) {
		if (index === null) {
			index = this._frame;
		}
		let imgData = this.getKeyFrameImageDataAt(index);
		if (imgData === null || imgData.clear || imgData.img === null) {
			return true;
		}

		let img = this._images.cache.get(imgData.img.key);
		return Boolean(img && (img.complete || img === BLANK_IMAGE));
	}

	/**
	 * Returns layer opacity as percent
	 *
	 * @returns {number}
	 */
	getOpacity() {
		return this._opacity;
	}

	/**
	 * @returns {string} see CanvasBlendingMode
	 */
	getBlendingMode() {
		return this._blendingMode;
	}

	/**
	 * Makes visible/invisible layer
	 *
	 * @param {?boolean} [flag]
	 * @returns {CanvasLayer}
	 */
	toggleVisibility(flag = null) {
		this._visible = flag !== null ? flag === true : !this._visible;
		this.events.trigger(EVENT_VISIBILITY_CHANGED);
		return this;
	}

	/**
	 * Sets index of the current timeline frame
	 *
	 * @param {number} index
	 * @return {CanvasLayer}
	 */
	setFrame(index) {
		if (this._frame === index) {
			return this;
		}
		this._frame = index;
		this._updateCurrentFrameImage();
		return this;
	}

	/**
	 * Returns current frame index
	 *
	 * @returns {number}
	 */
	getFrame() {
		return this._frame;
	}

	/**
	 * Returns list of the frames statues:
	 *  0 - empty frame
	 *  1 - keyframe
	 *  2 - repeat frame
	 *  3 - blank frame
	 *
	 * @returns {Array}
	 */
	getFramesStatuses() {
		let out = [];
		let tl = this._getTimeline(), isFilled = false;
		for (let i = 0, l = tl.length; i < l; i++) {
			if (tl[i].img === null) {
				if (tl[i].clear) {
					out.push(3);
					isFilled = false;
				} else {
					out.push(isFilled ? 2 : 0);
				}
			} else {
				out.push(1);
				isFilled = true;
			}
		}
		return out;
	}

	/**
	 * Returns Image Data of the last keyframe for the specified index
	 *
	 * @param {number} index
	 * @return {?T_ImageData}
	 */
	getKeyFrameImageDataAt(index) {
		let frames = this._getTimeline(),
			frm = frames[index],
			isKey = frm.img !== null || frm.clear;

		if (isKey) {
			return frm;
		} else {
			for (let f = index - 1; f >= 0; f--) {
				frm = frames[f];
				if (frm.img !== null || frm.clear) {
					return frm;
				}
			}
			return null;
		}
	}

	/**
	 * Returns closes keyframe index
	 *
	 * @param {number} index
	 * @return {?number}
	 */
	getClosestKeyFrameAt(index) {
		let frames = this._getTimeline(),
			frm = frames[index],
			isKey = frm.img !== null || frm.clear;

		if (isKey) {
			return index;
		} else {
			for (let f = index - 1; f >= 0; f--) {
				frm = frames[f];
				if (frm.img !== null || frm.clear) {
					return f;
				}
			}
			return null;
		}
	}

	/**
	 * Returns index of the
	 *
	 * @returns {?number}
	 */
	getPreviousKeyframeIndex() {
		if (this._frame === 0) {
			return null;
		}
		let index = this.getClosestKeyFrameAt(this._frame);
		if (index === null || index === 0) {
			return null;
		}
		return this.getClosestKeyFrameAt(index - 1);
	}

	/**
	 * Returns status of the frame:
	 *  0 - empty frame
	 *  1 - keyframe
	 *  2 - repeat frame
	 *  3 - blank frame
	 *
	 * @returns {number}
	 */
	getCurrentFrameStatus() {
		let frames = this._getTimeline(),
			frm = frames[this._frame],
			lastKeyFrm = this.getKeyFrameImageDataAt(this._frame);
		if (lastKeyFrm === null) {
			return 0;
		}
		if (frm === lastKeyFrm) {
			if (frm.clear) {
				// blank frame
				return 3;
			} else {
				// keyframe
				return 1;
			}
		} else {
			if (lastKeyFrm.img !== null) {
				// repeat frame
				return 2;
			} else if (lastKeyFrm.clear) {
				// no frame
				return 0;
			}
			// no frame
			return 0;
		}
	}

	/**
	 * Draws current frame into the specified canvas
	 *
	 * @param {T_CanvasElement} canvas
	 * @param {?number} [index=null] By default is current
	 * @param {boolean} [applyAlpha=true]
	 * @param {number} x
	 * @param {number} y
	 * @param {number} w
	 * @param {number} h
	 */
	drawFrame(canvas, index = null, applyAlpha = true, x = 0, y = 0, w = 0, h = 0) {
		let img = this.getImage(index),
			transform = this.getTransform(index);

		if (img && img.complete && transform) {
			let ctx = canvas.ctx,
				el = canvas.el;

			ctx.save();

			if (applyAlpha) {
				ctx.globalAlpha = this._opacity / 100;
			}

			ctx.globalCompositeOperation = CanvasBlendingModeToCompositeOperation[this._blendingMode];

			if (transform.flipX) {
				ctx.translate(el.width, 0);
				ctx.scale(-1, 1);
			}

			ctx.drawImage(
				img,
				x,
				y,
				w || el.width,
				h || el.height,
				x + (transform.flipX ? -1 : 1) * (transform.translateX || 0),
				y + (transform.flipY ? -1 : 1) * (transform.translateY || 0),
				w || el.width,
				h || el.height
			);
			ctx.restore();
		}
	}

	/**
	 * Returns transform values for the specified timeline frame
	 *
	 * @param {?number} [index]
	 * @returns {ImageTransform}
	 */
	getTransform(index = null) {
		index = this.getClosestKeyFrameAt(index === null ? this._frame : index);

		if (index === null) {
			// no keyframe found. return first transform object
			return this._timeline[0].transform;
		}

		return this._getTimeline()[index].transform;
	}

	/**
	 * Returns layer image of the timeline frame, by default current frame
	 *
	 * @param {?number} [index]
	 * @returns {?HTMLImageElement}
	 */
	getImage(index = null) {
		if (index === null) {
			index = this._frame;
		}
		let imgData = this.getKeyFrameImageDataAt(index);
		if (imgData === null || imgData.clear || imgData.img === null) {
			return null;
		}

		return this._getImageForData(imgData);
	}

	/**
	 * Returns layer image key
	 *
	 * @param {?number} [index]
	 * @returns {string}
	 */
	getImageKey(index = null) {
		if (index === null) {
			index = this._frame;
		}
		let imgData = this.getKeyFrameImageDataAt(index);
		if (imgData === null || imgData.clear || imgData.img === null) {
			return '';
		}

		return imgData.img.key;
	}

	/**
	 * Returns instance of the Image for the specified data
	 *
	 * @param {T_ImageData} imgData
	 * @private
	 */
	_getImageForData(imgData) {
		if (imgData.img.data === '') {
			return BLANK_IMAGE;
		}

		let images = this._images;
		let img = images.cache.get(imgData.img.key);
		if (!img) {
			img = new Image();
			img.crossOrigin = 'anonymous';
			img.addEventListener('load', this._handlers.imageLoad);
			img.addEventListener('error', () => {
				setTimeout(() => {
					console.log('Repeat image load');
					images.cache.delete(imgData.img.key);
					this._getImageForData(imgData);
				}, 500)
			});
			images.cache.set(imgData.img.key, img);
			img.src = imgData.img.data;
		}
		return img;
	}

	/**
	 * Handles image load
	 *
	 * @private
	 * @returns {void}
	 */
	_onImageLoaded() {
		// call all handlers from queue and remove them
		while (this._images.loadQueue.length > 0) {
			this._images.loadQueue.shift()(this);
		}

		this.events.trigger(EVENT_FRAME_LOADED, this);
	}

	/**
	 * Returns promise which will be resolved when all images in the frame loaded
	 *
	 * @param {number} index
	 * @returns {Promise<CanvasLayer>}
	 */
	getImagePromise(index) {
		let img = this.getImage(index);
		return new Promise((resolve) => {
			if (img === null || img === BLANK_IMAGE) {
				resolve(this);
				return;
			}
			if (img.complete || img.src === '') {
				resolve(this);
				return;
			}
			this._images.loadQueue.push(resolve);
		});
	}

	/**
	 * Enables temp mode for timeline. All changes will be added to the temp timeline.
	 *
	 * @param {boolean} flag
	 * @returns {CanvasLayer}
	 */
	enableTempTimeline(flag) {
		this._isTempMode = flag === true;
		if (this._isTempMode) {
			this.resetTempTimeline();
		} else {
			this._timelineTemp = null;
		}
		this.version++;
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Resets all changes in the temp timeline array. Restores original timeline.
	 *
	 * @returns {CanvasLayer}
	 */
	resetTempTimeline() {
		this._timelineTemp = [];
		for (let item of this._timeline) {
			this._timelineTemp.push({
				img: item.img,
				clear: item.clear,
				transform: item.transform.clone()
			});
		}
		this._clearCache();
		return this;
	}

	/**
	 * Returns right timeline array
	 *
	 * @return {Array<T_ImageData>}
	 * @private
	 */
	_getTimeline() {
		return this._isTempMode ? this._timelineTemp : this._timeline;
	}

	/**
	 * Changes keyframe position
	 *
	 * @param {number} fromIndex
	 * @param {number} toIndex
	 * @return {CanvasLayer}
	 */
	moveKeyFrame(fromIndex, toIndex) {
		if (!this.isOperable) {
			// this layer is not operable
			return this;
		}

		if (fromIndex === toIndex) {
			return this;
		}

		let tl = this._getTimeline();
		if (tl[fromIndex].img === null && !tl[fromIndex].clear) {
			return this;
		}
		tl[toIndex] = tl[fromIndex];
		tl[fromIndex] = {
			img: null,
			clear: false,
			transform: new ImageTransform()
		};
		this.version++;
		this._clearCache();
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Makes keyframes or blank keyframes
	 *
	 * @param {number} [fromFrame] Default is current frame
	 * @param {number} [toFrame] Default is fromFrame
	 * @returns {CanvasLayer}
	 */
	makeKeyFrame(fromFrame = -1, toFrame = -1) {
		if (!this.isOperable) {
			return this;
		}

		if (fromFrame < 0) {
			fromFrame = this._frame;
		}

		if (toFrame < 0) {
			toFrame = fromFrame;
		}

		let tl = this._getTimeline(), prevFrame;

		for (let f = fromFrame; f <= toFrame; f++) {
			let frame = tl[f];
			if (frame.img === null && !frame.clear) {
				if (!prevFrame) {
					// make blank keyframe by default and try to find filled keyframe
					frame.clear = true;
					if (f > 0) {
						let kfIndex = this.getClosestKeyFrameAt(f - 1);
						if (kfIndex !== null) {
							prevFrame = tl[kfIndex];
						}
					}
				}
				if (prevFrame) {
					frame.img = prevFrame.img;
					frame.clear = prevFrame.clear;
					frame.transform = prevFrame.transform;
				}
			} else {
				prevFrame = frame;
			}
		}
		this.version++;
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Makes clean frames, without key
	 *
	 * @param {number} [fromFrame] Default is current frame
	 * @param {number} [toFrame] Default is fromFrame
	 * @returns {CanvasLayer}
	 */
	makeCleanFrame(fromFrame = -1, toFrame = -1) {
		if (!this.isOperable) {
			return this;
		}

		if (fromFrame < 0) {
			fromFrame = this._frame;
		}

		if (toFrame < 0) {
			toFrame = fromFrame;
		}

		let tl = this._getTimeline();

		for (let f = fromFrame; f <= toFrame; f++) {
			let frame = tl[f];
			if (frame.img === null) {
				if (frame.clear) {
					// cancel blank frame
					frame.clear = false;
				}
				continue;
			}
			frame.clear = false;
			frame.img = null;
			if (f === this._frame) {
				this._onImageLoaded();
			}
		}
		this.version++;
		this._clearCache();
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Makes blank key frame
	 *
	 * @param {number} [fromFrame] Default is current frame
	 * @param {number} [toFrame] Default is fromFrame
	 * @returns {CanvasLayer}
	 */
	makeBlankKeyFrame(fromFrame = -1, toFrame = -1) {
		if (!this.isOperable) {
			return this;
		}

		if (fromFrame < 0) {
			fromFrame = this._frame;
		}

		if (toFrame < 0) {
			toFrame = fromFrame;
		}

		let tl = this._getTimeline();

		for (let f = fromFrame; f <= toFrame; f++) {
			let frame = tl[f];
			frame.clear = true;
			frame.img = null;
			frame.transform = new ImageTransform();
			if (f === this._frame) {
				this._onImageLoaded();
			}
		}

		this.version++;
		this._clearCache();
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Returns images data for the specified range of frames
	 *
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @returns {T_ImageData[]}
	 */
	getFramesImageData(fromFrame, toFrame) {
		if (fromFrame < 0) {
			fromFrame = this._frame;
		}

		if (toFrame < 0) {
			toFrame = this._frame;
		}

		const out = [];
		for (const frm of this._getTimeline().slice(fromFrame, toFrame + 1)) {
			out.push({
				img: frm.img,
				clear: frm.clear,
				transform: frm.transform.clone(),
			});
		}
		return out;
	}

	/**
	 * Sets images data for the frames
	 *
	 * @param {number} fromFrame
	 * @param {Array<T_ImageData>} imageDataList
	 * @returns {CanvasLayer}
	 */
	setFramesImageData(fromFrame, imageDataList) {
		if (!this.isOperable) {
			return this;
		}

		const timeline = this._getTimeline();
		for (let f = 0; f < imageDataList.length; f++) {
			const frm1 = timeline[fromFrame + f];
			const frm2 = imageDataList[f];
			frm1.img = frm2.img;
			frm1.clear = frm2.clear;
			frm1.transform = frm2.transform.clone();
		}

		this.version++;
		this._clearCache();
		this._updateCurrentFrameImage();
		return this;
	}

	/**
	 * Sets image for current timeline frame
	 *
	 * @param {string} dataURL
	 * @returns {CanvasLayer}
	 */
	setImage(dataURL) {
		if (!this.isOperable) {
			return this;
		}
		let frame = this._getTimeline()[this._frame];
		let data = this._canvas.dataSet.setData(dataURL);

		if (frame.img === data) {
			if (frame.clear) {
				// cancel blank frame
				frame.clear = false;
				this.version++;
				this._events.trigger(EVENT_FILL_FRAME);
			}
			return this;
		}
		frame.img = data;
		frame.clear = false;
		this.version++;
		this._clearCache();
		this._updateCurrentFrameImage();
		return this;
	}

	/**
	 * Updates images of the current frame
	 *
	 * @private
	 * @returns {void}
	 */
	_updateCurrentFrameImage() {
		if (!this.isLoaded()) {
			this.getImage();
		} else {
			setTimeout(this._handlers.imageLoad, 0);
		}
		this._events.trigger(EVENT_FILL_FRAME);
	}

	/**
	 * Returns TRUE if the repeat/empty frame can be inserted in the current timeline position
	 *
	 * @returns {boolean}
	 */
	canInsertFrame() {
		if (!this.isOperable) {
			return false;
		}

		let tl = this._getTimeline();
		if (this._frame >= tl.length - 1) {
			return false;
		}
		let lastFrame = tl[tl.length - 1];
		if (lastFrame.img !== null || lastFrame.clear) {
			// the last frame is not empty
			return false;
		}
		// check if there are keyframes at right side after current frame
		for (let f = this._frame + 1, l = tl.length; f < l; f++) {
			let frm = tl[f];
			if (frm.img !== null || frm.clear) {
				// all ok
				return true;
			}
		}
		// there are no keyframes at right
		return false;
	}

	/**
	 * Inserts new repeat/empty frame to current position and shifts all frames to right on 1 frame cell
	 *
	 * @returns {CanvasLayer}
	 */
	insertFrame() {
		if (!this.canInsertFrame()) {
			return this;
		}
		let tl = this._getTimeline();
		for (let f = tl.length - 2; f > this._frame; f--) {
			tl[f + 1] = tl[f];
		}
		tl[this._frame + 1] = {img: null, clear: false, transform: new ImageTransform()};
		this.version++;
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Removes frame at the current position and shifts all frames to left on 1 frame cell
	 *
	 * @param {number} [fromFrame] Default is current frame
	 * @param {number} [toFrame] Default is fromFrame
	 * @returns {CanvasLayer}
	 */
	removeFrame(fromFrame = -1, toFrame = -1) {
		if (!this.isOperable) {
			return this;
		}

		if (fromFrame < 0) {
			fromFrame = this._frame;
		}

		if (toFrame < 0) {
			toFrame = fromFrame;
		}

		let tl = this._getTimeline();
		let count = this._canvas.getTotalFrames();
		tl.splice(fromFrame, toFrame - fromFrame + 1);
		while (tl.length < count) {
			tl.push({img: null, clear: false, transform: new ImageTransform()});
		}
		this.version++;
		this._clearCache();
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Gets layer properties
	 *
	 * @returns {T_LayerProps}
	 */
	getProps() {
		return {
			title: this._title,
			isVisible: this._visible,
			opacity: this._opacity,
			blend: this._blendingMode
		};
	}

	/**
	 * Sets layer properties
	 *
	 * @param {T_LayerProps} data
	 * @returns {CanvasLayer}
	 */
	updateProps(data) {
		this._title = data.title;
		this._visible = data.isVisible === true;
		this._opacity = Math.min(100, Math.max(10, parseInt(String(data.opacity))));
		this._blendingMode = data.blend;
		this.version++;
		return this;
	}

	/**
	 * Returns current state data of the layer
	 *
	 * @returns {T_StateLayer}
	 */
	getState() {
		if (this._prevLayerState !== null) {
			if (this._prevLayerState.version === this.version) {
				return this._prevLayerState;
			}
		}
		let frames = [];
		for (let frame of this._timeline) {
			frames.push({
				key: frame.img === null ? '' : frame.img.key,
				clear: frame.clear,
				transform: frame.transform.clone()
			});
		}
		this._prevLayerState = {
			id: this._id,
			title: this._title,
			version: this.version,
			frames
		};
		return this._prevLayerState;
	}

	/**
	 * Restores layer state from the previously saved data
	 *
	 * @param {T_StateLayer} data
	 * @returns {void}
	 */
	restoreState(data) {
		if (data.version === this.version) {
			return;
		}
		this._title = data.title;
		this.version++; // fix issue #1
		let tl = this._timeline, dataSet = this._canvas.dataSet;
		for (let a = 0, l = data.frames.length; a < l; a++) {
			let item1 = data.frames[a], item2 = tl[a];
			item2.clear = item1.clear;
			if (this._sourceId !== '') {
				item2.img = item1.key === ''
					? null
					: this._getDataSetItemForReferenceFrame(parseInt(item1.key.match(/_frm_(\d+)/)[1]));
			} else {
				item2.img = item1.key === '' ? null : dataSet.getData(item1.key);
			}
			item2.transform = item1.transform.clone();

			if (this._frame === a) {
				this.getImage();
			}
		}

		this._clearCache();
		this._events.trigger(EVENT_FILL_FRAME);
	}

	/**
	 * Returns layer's data
	 *
	 * @param {boolean} [generateNewId]
	 * @param {boolean} [onlyDelta] Do not include sources for the frames that was not changed
	 * @returns {Promise<T_CanvasLayerData>}
	 */
	async getData(generateNewId = false, onlyDelta = true) {
		let srcFrames = this._timeline,
			frames = [], indices = [], transform = [],
			sources = {'': ''}, dataKey, lastDataKey = '',
			sourceMap = new Map();

		if (this._sourceId === '') {
			for (let frm of srcFrames) {
				if (frm.img === null) {
					frames.push(frm.clear ? 1 : 0);
					transform.push(frm.transform.getData());
					indices.push(frm.clear ? '' : lastDataKey);
				} else {
					dataKey = sourceMap.get(frm.img.key);
					if (!dataKey) {
						dataKey = Utils.getHash(frm.img.data);
						if (!onlyDelta || !this._initialIndices.includes(dataKey)) {
							sources[dataKey] = frm.img.data;
						}
						sourceMap.set(frm.img.key, dataKey);
					}
					lastDataKey = dataKey;
					indices.push(dataKey);
					transform.push(frm.transform.getData());
					frames.push(2);
				}
			}
		} else {
			// this is a reference scene layer, it does not has own graphics info
			indices = new Array(srcFrames.length).fill('');
			frames = new Array(srcFrames.length).fill(0);
			for (let frm of srcFrames) {
				transform.push(frm.transform.getData());
			}
		}

		return {
			id: generateNewId ? CanvasLayer.generateId() : this._id,
			title: this._title,
			opacity: this._opacity,
			blend: this._blendingMode,
			source_id: this._sourceId,
			transform,
			sources,
			frames,
			sprites: this._sprites,
			indices
		};
	}

	/**
	 * Fills currently used Data Set items
	 *
	 * @param {Set<string>} fillMap
	 * @returns {void}
	 */
	fillDataSetKeys(fillMap) {
		if (this._sourceId !== '') {
			// this layer does not has own graphics information
			return;
		}

		for (let frm of this._timeline) {
			if (frm.img) {
				fillMap.add(frm.img.key);
			}
		}
	}

	/**
	 * Merges the specified layer into this one
	 *
	 * @param {CanvasLayer} layer
	 * @returns {Promise<void>}
	 */
	mergeLayer(layer) {
		if (!this.isOperable) {
			throw new Error('Cannot merge layer into not operable layer');
		}

		if (!layer.isOperable) {
			throw new Error('Cannot merge not operable layer into another layer');
		}

		let canvas = document.createElement('canvas');
		canvas.width = this._canvas.sceneWidth;
		canvas.height = this._canvas.sceneHeight;
		let ctx = canvas.getContext('2d'), promises = [];
		let prevKeyFrame1, prevKeyFrame2;
		for (let f = 0, l = this._timeline.length - 1; f <= l; f++) {
			let frame1 = this._timeline[f];
			let frame2 = layer._timeline[f];

			let isKeyFrame1 = frame1.clear || frame1.img !== null;
			if (isKeyFrame1 || !prevKeyFrame1) {
				prevKeyFrame1 = frame1;
			}
			let isKeyFrame2 = frame2.clear || frame2.img !== null;
			if (isKeyFrame2 || !prevKeyFrame2) {
				prevKeyFrame2 = frame2;
			}

			if (!isKeyFrame1 && !isKeyFrame2) {
				// both frames have no information
				continue;
			}

			promises.push(
				this._mergeDataItems(canvas, ctx, prevKeyFrame1.img, prevKeyFrame2.img, layer)
					.then((item) => {
						frame1.clear = false;
						frame1.img = item;
					})
			);
		}
		return Promise.all(promises).then(() => {
			this.version++;
			this._clearCache();
			this._updateCurrentFrameImage();
		});
	}

	/**
	 * Merges two DataSet Items (two images)
	 *
	 * @param {HTMLCanvasElement} canvas
	 * @param {CanvasRenderingContext2D} ctx
	 * @param {T_DataSetItem} frameData1
	 * @param {T_DataSetItem} frameData2
	 * @param {CanvasLayer} layer
	 * @returns {Promise<T_DataSetItem>}
	 * @private
	 */
	_mergeDataItems(canvas, ctx, frameData1, frameData2, layer) {
		return new Promise((resolve) => {
			Promise.all([
				this._loadFrameImage(frameData1),
				this._loadFrameImage(frameData2)
			]).then((images) => {
				ctx.clearRect(0, 0, this._canvas.sceneWidth, this._canvas.sceneHeight);
				ctx.drawImage(images[0], 0, 0);

				if (layer.getOpacity() !== 100) {
					ctx.globalAlpha = layer.getOpacity() / 100;
				}

				if (layer.getBlendingMode() !== CanvasBlendingMode.NORMAL) {
					ctx.globalCompositeOperation = CanvasBlendingModeToCompositeOperation[layer.getBlendingMode()];
				}

				ctx.drawImage(images[1], 0, 0);
				resolve(this._canvas.dataSet.setData(canvas.toDataURL('image/png')));
			});
		});
	}

	/**
	 * Updates transform values for the frames range
	 *
	 * @param {ImageTransform} transform
	 * @param {number} [fromFrame]
	 * @param {number} [toFrame]
	 */
	updateTransformForFrames(transform, fromFrame = -1, toFrame = -1) {
		[fromFrame, toFrame] = this._correctFramesRange(fromFrame, toFrame);
		for (let i = fromFrame; i <= toFrame; i++) {
			let frame = this._timeline[i];
			if (frame.img !== null && frame.img.data !== '' && !frame.clear) {
				frame.transform.mergeFrom(transform);
			}
		}
		this.version++;
	}

	/**
	 * Updates the specified range of keyframes using callback function
	 *
	 * @param {function(img:HTMLImageElement, frameIndex:number):?T_DataSetItem} handler
	 * @param {number} [fromFrame]
	 * @param {number} [toFrame]
	 * @param {boolean} [researchMode=false] If true then handler is only make research and do not change images
	 * @returns {Promise}
	 */
	updateFrameImages(handler, fromFrame = -1, toFrame = -1, researchMode = false) {
		if (!this.isOperable && !researchMode) {
			throw new Error('Cannot apply frames filter to not operable layer');
		}

		let queue = [];
		[fromFrame, toFrame] = this._correctFramesRange(fromFrame, toFrame);
		for (let i = fromFrame; i <= toFrame; i++) {
			let frame = this._timeline[i];
			if (frame.img !== null && frame.img.data !== '' && !frame.clear) {
				queue.push(this._updateFrameImage(handler, frame, i));
			}
		}

		let promise = Promise.all(queue);
		if (!researchMode) {
			promise.then(() => {
				this.version++;
				this._clearCache();
				this._updateCurrentFrameImage();
			});
		}

		return promise;
	}

	/**
	 * Loads image and returns promise
	 *
	 * @param {T_DataSetItem|null} dataItem
	 * @returns {Promise<Image>}
	 * @private
	 */
	_loadFrameImage(dataItem) {
		return new Promise((resolve, reject) => {
			let img = new Image();
			img.crossOrigin = 'anonymous';
			img.addEventListener('load', () => {
				resolve(img);
			});
			img.addEventListener('error', () => {
				console.error('Cannot load frame image for updating. Just skip it');
				reject();
			});
			img.src = (!dataItem || dataItem.data === '') ? this._canvas.blankImageData : dataItem.data;
		});
	}

	/**
	 * Updates image in frame
	 *
	 * @param {function(img:HTMLImageElement, frameIndex:number):?T_DataSetItem} handler
	 * @param {T_ImageData} frame
	 * @param {number} frameIndex
	 * @returns {Promise<void>}
	 * @private
	 */
	_updateFrameImage(handler, frame, frameIndex) {
		return new Promise((resolve) => {
			this._loadFrameImage(frame.img).then(
				(img) => {
					const data = handler(img, frameIndex);

					if (data !== null && data !== undefined) {
						frame.img = data;
					}

					resolve();
				},
				() => {
					// ignore errors
					resolve();
				}
			);
		});
	}

	/**
	 * Fills the specified range of frames using callback function
	 *
	 * @param {function():T_DataSetItem} handler
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @returns {void}
	 */
	fillFrames(handler, fromFrame, toFrame) {
		if (!this.isOperable) {
			throw new Error('Cannot fill frames in not operable layer');
		}

		let data;
		[fromFrame, toFrame] = this._correctFramesRange(fromFrame, toFrame);
		for (let i = fromFrame; i <= toFrame; i++) {
			let frame = this._timeline[i];
			frame.clear = false;
			data = handler();
			frame.img = data;
		}
		if (this._frame >= fromFrame && this._frame <= toFrame) {
			this._updateCurrentFrameImage();
		}
		this.version++;
		this._clearCache();
	}

	/**
	 * Corrects frame range: from and to frame indexes
	 *
	 * @param {number} fromFrame
	 * @param {number} toFrame
	 * @returns {Array}
	 * @private
	 */
	_correctFramesRange(fromFrame, toFrame) {
		if (fromFrame < 0) {
			fromFrame = this._frame;
		}

		if (toFrame < 0) {
			toFrame = this._frame;
		}

		fromFrame = Math.max(0, fromFrame);
		toFrame = Math.min(this._timeline.length - 1, toFrame);
		return [fromFrame, toFrame];
	}

	/**
	 * Returns index of the last keyframe in timeline.
	 * Returns -1 if there are no keyframes
	 *
	 * @returns {number}
	 */
	getLastKeyFrameIndex() {
		for (let i = this._timeline.length - 1; i >= 0; i--) {
			let frame = this._timeline[i];
			if (frame.clear || frame.img !== null) {
				return i;
			}
		}
		return -1;
	}

	invertFrames(fromFrame = -1, toFrame = -1) {
		if (!this.isOperable) {
			return this;
		}

		if (fromFrame < 0) {
			fromFrame = 0;
		}

		if (toFrame < 0) {
			toFrame = this.getLastKeyFrameIndex();
		}

		const slice = this._timeline.splice(fromFrame, toFrame - fromFrame + 1);
		slice.reverse();
		this._timeline.splice(fromFrame, 0, ...slice);

		this.version++;
		this._clearCache();
		this._events.trigger(EVENT_FILL_FRAME);
	}


	/**
	 * Repeats current list of frames
	 *
	 * @param {number} [fromFrame]
	 * @param {number} [toFrame]
	 * @param {number} [times=0]
	 * @param {boolean} [isReflected=false]
	 * @returns {CanvasLayer}
	 */
	repeatFrames(fromFrame = -1, toFrame = -1, times = 0, isReflected = false) {
		if (!this.isOperable) {
			return this;
		}

		if (fromFrame < 0) {
			fromFrame = 0;
		}

		if (toFrame < 0) {
			toFrame = this.getLastKeyFrameIndex();
		}

		let i = 0, maxI = (toFrame - fromFrame) * times;

		let indices = [];
		for (let f = fromFrame; f < toFrame + 1; f++) {
			indices.push(f);
		}

		if (isReflected) {
			// make 1,2,3,4 -> 4,3,2,1
			indices.reverse();
			// make 4,3,2,1 -> 4,3,2,1,2,3
			indices = indices.concat([...indices].slice(1, -1).reverse());
			// make 4,3,2,1,2,3 -> 3,2,1,2,3,4
			indices.push(indices.shift());
			maxI = maxI * 2 - 1;
		}

		for (let f = toFrame + 1; f < this._timeline.length; f++) {
			const frameFrom = this._timeline[indices[i % indices.length]];
			const frameTo = this._timeline[f];

			frameTo.clear = frameFrom.clear;
			frameTo.img = frameFrom.img;
			frameTo.transform = frameFrom.transform.clone();

			++i;

			if (times > 0 && i > maxI) {
				break;
			}
		}

		this.version++;
		this._clearCache();
		this._events.trigger(EVENT_FILL_FRAME);
		return this;
	}

	/**
	 * Builds timeline from the received layer data
	 *
	 * @param {T_CanvasLayerData} data
	 * @private
	 */
	_buildReferenceTimeline(data) {
		let out = [],
			prevFrm = -1,
			totalFrames = this._canvas.getTotalFrames(),
			sprites = data.sprites,
			transform = data.transform || new Array(totalFrames).fill(''),
			indices = sprites.index;

		for (let a = 0, i = 0, l = totalFrames; a < l; a++, i++) {
			let frm = indices[a];
			if (frm === prevFrm) {
				// clean/repeat frame
				out.push({img: null, clear: false, transform: new ImageTransform(transform[a])});
			} else {
				// filled key frame
				out.push({
					img: this._getDataSetItemForReferenceFrame(frm),
					clear: false,
					transform: new ImageTransform(transform[a])
				});
			}
			prevFrm = frm;
		}
		this._timeline = out;
		this._clearCache();
	}

	/**
	 * Returns data set item for the specified reference frame index
	 *
	 * @param {number} frm
	 * @returns {T_DataSetItem}
	 * @private
	 */
	_getDataSetItemForReferenceFrame(frm) {
		let framesUrl = this._sprites ? this._sprites.frames_url : '';
		return {
			key: `s_${this._sourceId}_frm_${frm}`,
			checkKey: `s_${this._sourceId}_frm_${frm}`,
			data: STATIC_URL + '/data/' + framesUrl + '/frame_' + frm + '.png'
		};
	}

	/**
	 * Builds timeline from the received layer data
	 *
	 * @param {T_CanvasLayerData} data
	 * @private
	 */
	_buildTimeline(data) {
		let out = [],
			dataSet = this._canvas.dataSet,
			totalFrames = this._canvas.getTotalFrames(),
			sources = data.sources || {'': ''},
			indices = data.indices || new Array(totalFrames).fill(''),
			transform = data.transform || new Array(totalFrames).fill(''),
			frames = data.frames || new Array(totalFrames).fill(0);

		for (let a = 0, i = 0, l = totalFrames; a < l; a++, i++) {
			let frm = frames[a];
			if (frm === 0) {
				// clean frame
				out.push({img: null, clear: false, transform: new ImageTransform(transform[a])});
			} else if (frm === 1 && indices[a] === '') {
				// blank key frame
				out.push({img: null, clear: true, transform: new ImageTransform(transform[a])});
			} else {
				// filled key frame
				this._initialIndices.push(indices[i]);
				out.push({
					img: dataSet.setData(sources[indices[i]]),
					clear: false,
					transform: new ImageTransform(transform[a])
				});
			}
		}
		this._timeline = out;
		this._clearCache();
	}

	/**
	 * Clears images cache
	 *
	 * @private
	 */
	_clearCache() {
		let keys = [];

		this._timeline.forEach((item) => {
			if (item.img) {
				keys.push(item.img.key);
			}
		});

		for (let key of this._images.cache.keys()) {
			if (!keys.includes(key)) {
				this._images.cache.delete(key);
			}
		}
	}
}
